import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useSearchParams } from "react-router-dom";

import {
  useLazyGetOrganizationByIdQuery,
  useLazyGetSurveyStatisticsQuery,
} from "@/redux/service.js";
import SubmissionConfirmationModal from "@/domains/survey/components/SubmissionConfirmationModal";
import { SurveySummaryProps } from "@/features/survey/summary/types";
import SectionCard from "@/features/survey/summary/components/SectionCard";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner.jsx";
import SurveyHeader from "@/features/survey/summary/components/SurveyHeader";
import { useOrganizationId } from "@/shared/hooks/useOrganizationId";
import { usePageBanner } from "@/shared/contexts/PageBannerContext";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import Warning from "@/assets/icons/warning-circle.svg?react";

import "./styles.css";

const SurveySummary = ({ survey }: SurveySummaryProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const currentOrganizationId = useOrganizationId();
  const { setBannerContent, clearBanner } = usePageBanner();
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [getSurveyStatistics, surveyStatisticsResponse] =
    useLazyGetSurveyStatisticsQuery();
  const [getOrganizationById] = useLazyGetOrganizationByIdQuery();
  const sampleOrganizationId = searchParams.get("sampleOrganizationId");

  useEffect(() => {
    getSurveyStatistics({
      survey: survey.id,
      organization: sampleOrganizationId || currentOrganizationId,
    });
  }, [survey]);

  useEffect(() => {
    if (!sampleOrganizationId) {
      clearBanner();
      return;
    }

    const fetchAndSetBanner = async () => {
      const organizationData = await getOrganizationById(sampleOrganizationId);
      setBannerContent({
        title: t("survey.labels.viewingSample"),
        description: t("survey.labels.viewingSampleDescription", {
          organizationName: organizationData.data?.name || "",
        }),
        icon: (
          <BaseSvgRenderer
            component={Warning}
            fill="var(--orange-500)"
            stroke="var(--base-white)"
          />
        ),
        titleColor: "var(--orange-500)",
        descriptionColor: "var(--orange-500)",
        backgroundColor: "#fff8f1",
      });
    };

    fetchAndSetBanner();

    return clearBanner;
  }, [sampleOrganizationId, sampleOrganizationId]);

  if (surveyStatisticsResponse.isLoading) {
    return (
      <div className="flex justify-center full-width">
        <BaseSpinner />
      </div>
    );
  }

  if (!survey || surveyStatisticsResponse.isError) {
    return <Navigate to="/" />;
  }

  return (
    surveyStatisticsResponse.isSuccess && (
      <div className="page-layout">
        <SurveyHeader
          survey={survey}
          statistics={surveyStatisticsResponse.data}
          setOpenConfirmationModal={setOpenConfirmationModal}
          isSample={!!sampleOrganizationId}
        />

        <div className="survey-sections-overview-content-container flex flex-col gap-400 items-start full-width">
          <div className="flex flex-col gap-200">
            <h3 className="header-m-medium">{t("survey.labels.principles")}</h3>

            <p className="body-m-regular">
              {t("survey.labels.clickToStartAnswering")}
            </p>
          </div>

          <div className="survey-sections-overview-content gap-400 full-width">
            {surveyStatisticsResponse.data
              .slice()
              .sort((a, b) => a.index - b.index)
              .map((section) => {
                return (
                  <SectionCard
                    isSample={!!sampleOrganizationId}
                    section={section}
                  />
                );
              })}
          </div>
        </div>

        <SubmissionConfirmationModal
          setOpen={setOpenConfirmationModal}
          survey={survey}
          open={openConfirmationModal}
        />
      </div>
    )
  );
};

export default SurveySummary;
